import PropTypes from 'prop-types'
import { useEffect, useContext } from 'react'
import { Link as RouterLink, useLocation } from 'react-router-dom'

import { styled } from '@mui/material/styles'
import { Box, Link, Drawer, Typography, Avatar } from '@mui/material'

import account from '../../_mocks_/account'

import useResponsive from '../../hooks/useResponsive'
import useAuth from '../../hooks/useAuth'

import Logo from '../../components/Logo'
import Scrollbar from '../../components/Scrollbar'
import NavSection from '../../components/NavSection'

import superAdminSidebarConfig from './SuperAdminSidebarConfig'
import startupAdminSidebarConfig from './StartupAdminSidebarConfig'
import investorAdminSidebarConfig from './InvestorAdminSidebarConfig'
import conformerAdminSidebarConfig from './ConformerAdminSidebarConfig'
import supervisorSidebarConfig from './SupervisorSidebarConfig'

import NotificationContext from '../../contexts/notification/NotificationContext'
import NotificationType from '../../contexts/notification/NotificationType'
import { getNotifications } from '../../contexts/notification/NotificationActions'

const DRAWER_WIDTH = 280

const RootStyle = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('lg')]: {
    flexShrink: 0,
    width: DRAWER_WIDTH,
  },
}))

const AccountStyle = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(2, 2.5),
  borderRadius: Number(theme.shape.borderRadius) * 1.5,
  backgroundColor: theme.palette.grey[500_12],
}))

export default function DashboardSidebar({ isOpenSidebar, onCloseSidebar }) {
  const { pathname } = useLocation()
  const { user } = useAuth()
  const { notifications, dispatch } = useContext(NotificationContext)

  const isDesktop = useResponsive('up', 'lg')

  useEffect(() => {
    if (isOpenSidebar) {
      onCloseSidebar()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])

  useEffect(() => {
    fetchNotifications()
  }, [])

  const fetchNotifications = async () => {
    try {
      const notificationsData = await getNotifications()
      dispatch({
        type: NotificationType.GET_NOTIFICATIONS,
        payload: notificationsData,
      })
    } catch (error) {
      console.log(error)
    }
  }

  const showUserRole = (role) => {
    if (role.includes('SUPERADMIN')) {
      return 'مدیر اصلی'
    } else if (role.includes('CONFORMERADMIN')) {
      return 'مدیر تعیین کننده'
    } else if (role.includes('STARTUPADMIN')) {
      return 'مدیر استارتاپ'
    } else if (role.includes('INVESTORADMIN')) {
      return 'مدیر سرمایه گذار'
    } else if (role.includes('SUPERVISOR')) {
      return 'سرپرست'
    }
  }

  const renderContent = (
    <Scrollbar
      sx={{
        height: 1,
        '& .simplebar-content': {
          height: 1,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Box sx={{ px: 2.5, py: 3, display: 'inline-flex' }}>
        <Logo />
      </Box>

      <Box sx={{ mb: 5, mx: 2.5 }}>
        <Link underline='none' component={RouterLink} to='#'>
          <AccountStyle>
            <Avatar src={account.photoURL} alt='photoURL' />
            <Box sx={{ ml: 2 }}>
              <Typography variant='subtitle2' sx={{ color: 'text.primary' }}>
                {user.firstName} {user.lastName}
              </Typography>
              <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                {showUserRole(user.role)}
              </Typography>
            </Box>
          </AccountStyle>
        </Link>
      </Box>

      <NavSection
        navConfig={
          user.role.includes('SUPERADMIN')
            ? superAdminSidebarConfig
            : user.role.includes('CONFORMERADMIN')
            ? conformerAdminSidebarConfig
            : user.role.includes('STARTUPADMIN')
            ? startupAdminSidebarConfig
            : user.role.includes('INVESTORADMIN')
            ? investorAdminSidebarConfig
            : user.role.includes('SUPERVISOR')
            ? supervisorSidebarConfig
            : ''
        }
        notifications={notifications}
      />

      <Box sx={{ flexGrow: 1 }} />
    </Scrollbar>
  )

  return (
    <RootStyle>
      {!isDesktop && (
        <Drawer
          open={isOpenSidebar}
          onClose={onCloseSidebar}
          PaperProps={{
            sx: { width: DRAWER_WIDTH },
          }}
        >
          {renderContent}
        </Drawer>
      )}

      {isDesktop && (
        <Drawer
          open
          variant='persistent'
          PaperProps={{
            sx: {
              width: DRAWER_WIDTH,
              bgcolor: 'background.default',
              borderRightStyle: 'dashed',
            },
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </RootStyle>
  )
}

DashboardSidebar.propTypes = {
  isOpenSidebar: PropTypes.bool,
  onCloseSidebar: PropTypes.func,
}
